import {Injectable, OnDestroy} from '@angular/core';
import {ApiPaginationResponse, ApiService} from '@abstract/api.service';
import {BehaviorSubject} from 'rxjs/internal/BehaviorSubject';
import {untilDestroyed} from 'ngx-take-until-destroy';
import {Subject} from 'rxjs/internal/Subject';
import {takeUntil} from 'rxjs/operators';
import {Direction, DirectionEnum} from '../enums/direction.enum';
import {BrowserStorageDataEnum, BrowserStorageService} from '@services/browser-storage.service';

/**
 * Api для пагинации с фильтрацией и сортировкой
 */
@Injectable()
export abstract class PagingApiService<T extends {id: number} = any, SortBy = string>
    extends ApiService implements OnDestroy {
    /**
     * Ключ, по которому из localStorage будет доставаться количество элементов на страницу
     */
    itemsPerPageStorageKey: BrowserStorageDataEnum;

    /**
     * По сколько элементов показывать на страницу, если не задано в localStorage
     */
    defaultItemsPerPage = 10;

    /**
     * Варианты для выбора, по сколько показывать на страницу
     * Используется чтобы во View показывать выбор количества отображаемых элементов
     */
    itemsPerPageCounts = [10, 25, 50];

    itemsPerPage = this.defaultItemsPerPage;

    fetching$ = new BehaviorSubject<boolean>(false);

    items$: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);
    page$ = new BehaviorSubject<number>(1);
    pagesTotal$ = new BehaviorSubject<number>(1);

    /**
     * Сколько найдено и сколько всего элементов
     */
    totalFoundCount$ = new BehaviorSubject<number>(0);
    totalItemsCount$ = new BehaviorSubject<number>(0);

    sortBy$ = new BehaviorSubject<SortBy|string>(null);
    sortDirection$ = new BehaviorSubject<DirectionEnum>(DirectionEnum.up);

    get pagingUrl(): string {
        return this.baseUrl;
    }

    get queryParams() {
        return Object.assign({}, this.getPagingParams(this.page$.getValue()), this._filters$.getValue(), this.getSortingParams());
    }

    get filters() {
        return this._filters$.getValue();
    }

    /**
     * Добавлять или нет параметры фильтров в URL?
     */
    addFiltersToUrl = false;

    private _filters$ = new BehaviorSubject<{[key: string]: string}>(this.getDefaultFiltersParams());

    private prevent$ = new Subject();

    public filters$ = this._filters$.asObservable();

    /**
     * Метод для инициализации и сайд эффектов, когда инициализируется компонент, использующий данный сервис
     */
    afterListComponentInit() {
        if (this.addFiltersToUrl) {
            this.setFiltersToUrl();
        }
    }

    getSortingParams() {

        const currentDirection = +this.sortDirection$.getValue();
        const sortByValue = this.sortBy$.getValue();

        let sortValue = '';

        if (sortByValue) {
            if (currentDirection === DirectionEnum.down) {
                sortValue += '-';
            }

            sortValue += sortByValue;
        }

        return {
            sort: sortValue
        };
    }

    setFilters(values: {[key: string]: string}) {
        /**
         * Удаляем все пустые значения из фильтров
         */
        for (const key of Object.keys(values)) {
            if (['', null, undefined].includes(values[key])) {
                delete values[key];
            }
        }

        this._filters$.next(values);

        /**
         * Обновляем параметры фильтров в адресной строке, если нужно
         */
        if (this.addFiltersToUrl) {
            this.setFiltersToUrl();
        }

        this.updateItems();
    }

    resetFilters() {
        this.setFilters(this.getDefaultFiltersParams());
    }

    /**
     * Стандартные настройки фильтрации
     */
    getDefaultFiltersParams() {
        return {};
    }

    getPagingParams(page: number): {page: number; items_per_page: number} {
        return {
            page,
            items_per_page: this.itemsPerPage
        };
    }

    setItemsPerPage(count: number) {
        /**
         * Пусть дефолтным значением будет это
         */
        let newCount = this.defaultItemsPerPage;

        if (count) {
            newCount = count;
            BrowserStorageService.set(this.itemsPerPageStorageKey, newCount);
        } else if (this.itemsPerPageStorageKey) {
            newCount = Number(BrowserStorageService.get(this.itemsPerPageStorageKey)) || this.defaultItemsPerPage;
        }

        this.itemsPerPage = newCount;
        this.updateItems();
    }

    setPage(page: number) {
        this.page$.next(page);
        this.updateItems();
    }

    setSort(sortBy: SortBy|string) {
        const currentSort = this.sortBy$.getValue();
        const currentDirection = +this.sortDirection$.getValue();

        if (currentSort === sortBy) {
            this.sortDirection$.next(Direction.toggle(currentDirection));
        } else {
            this.sortBy$.next(sortBy);
            this.sortDirection$.next(DirectionEnum.up);
        }

        this.updateItems();
    }

    /**
     * Прокидываем параметры в фильтры без запроса обновления списка элементов.
     * Используется в резолверах перед обновлением списка
     * @param params
     */
    parseQueryParams(params: {[key: string]: string}) {
        this._filters$.next(params);
    }

    /**
     * Установить параметры в адресную строку.
     * WARNING :: Использовать только на отрендеренной странице, а не в резолвере в момент перехода
     */
    setFiltersToUrl() {
        this._router.navigate([], {relativeTo: this._route, queryParams: this.filters});
    }

    updateItems() {
        this.prevent$.next(true);

        /**
         * Восстанавливаем количество элементов на страницу из стораджа перед запросом
         */
        const itemsPerPageFromStorage = Number(BrowserStorageService.get(this.itemsPerPageStorageKey));
        this.itemsPerPage = itemsPerPageFromStorage || this.defaultItemsPerPage;

        this.fetching$.next(true);

        this.get({
            url: this.pagingUrl,
            queryParams: this.queryParams,
        }).pipe(
            takeUntil(this.prevent$),
            untilDestroyed(this),
        ).subscribe((data) => {
            this.handlePagingDataReceive(data);
            this.fetching$.next(false);
        }, () => {
            this.handlePagingDataReceive(null);
            this.fetching$.next(false);
        });
    }

    ngOnDestroy(): void {
        this._filters$.complete();
        this.items$.complete();
        this.prevent$.complete();
        this.fetching$.complete();
        this.page$.complete();
        this.pagesTotal$.complete();
        this.sortBy$.complete();
        this.sortDirection$.complete();
        this.totalFoundCount$.complete();
        this.totalItemsCount$.complete();
    }

    resetParams() {
        this.page$.next(1);
        this._filters$.next(this.getDefaultFiltersParams());
        this.sortDirection$.next(DirectionEnum.up);
    }

    protected handlePagingDataReceive(data: ApiPaginationResponse<T>) {
        if (!data || !data.page || !data.items) {
            this.items$.next([]);
            this.page$.next(1);
            this.pagesTotal$.next(1);
            this.totalItemsCount$.next(0);
            this.totalFoundCount$.next(0);
            return;
        }

        this.items$.next(data.items);
        this.page$.next(data.page);
        this.pagesTotal$.next(data.pages_total);
        this.totalFoundCount$.next(data.total_found_count);
        this.totalItemsCount$.next(data.total_items_count);
    }
}
